import { useCallback, useState, useEffect } from 'react';
import {
  LeftCircleOutlined,
  PrinterOutlined,
  SignatureOutlined,
} from '@ant-design/icons';
import { AButton, ATitle, Loading, ToSignature } from '../../../components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../../../services/api';
import { useToast } from '../../../hooks/toast';
import { useConfiguration } from '../../../hooks/configuration';
import { IDocument } from '../../../shared/interfaces';
import { HeaderContainer, RowPrintDocument } from './styles';
import { DocumentReport } from '../../../shared/reports';
import { hasAllowedModule } from '../../../shared/utils/modules';

const DocumentPrint: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const { configuration } = useConfiguration();
  const [loading, setLoading] = useState<boolean>(true);
  const [report, setReport] = useState<IDocument | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const [modalSign, setModalSign] = useState<boolean>(false);
  const [validationCode, setValidationCode] = useState<string | null>(null);
  const [signatureMode, setSignatureMode] = useState<string | null>(null);

  const loadDocument = useCallback(async () => {
    try {
      const documentResponse = await api.get(`/clients-documents/${id}`);

      const reportData = documentResponse.data;
      const isDigitallySigned = Boolean(
        reportData?.signature_validation_code || reportData?.signed_at,
      );

      let signedProfessionalSignature: string | null = null;
      if (isDigitallySigned && reportData?.professional_id) {
        const professionalResponse = await api.get(
          `/professionals/${reportData.professional_id}`,
        );
        signedProfessionalSignature =
          professionalResponse.data?.signature || null;
      }

      setReport(reportData);
      setValidationCode(reportData?.signature_validation_code || null);
      setSignature(isDigitallySigned ? signedProfessionalSignature : null);
      setLoading(false);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao encontrar documento',
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, id]);

  useEffect(() => {
    loadDocument();
  }, [loadDocument]);

  if (loading) return <Loading load />;

  const isDigitallySigned = Boolean(validationCode || report?.signed_at);
  const canUseDigitalSignature = hasAllowedModule(
    configuration?.allowed_modules,
    'assinatura_digital',
  );
  const isPrescription =
    report?.kind === 'PRESCRIPTION' ||
    location.pathname.startsWith('/prescription/');

  return (
    <>
      <HeaderContainer>
        <ATitle icon={<PrinterOutlined />}>
          {isPrescription ? 'Imprimir Receituário' : 'Imprimir Documento'}
        </ATitle>
        <RowPrintDocument>
          <AButton
            type="default"
            onClick={() =>
              navigate(isPrescription ? '/prescriptions' : '/documents')
            }
            icon={<LeftCircleOutlined />}
          >
            Voltar
          </AButton>
          {!isDigitallySigned && canUseDigitalSignature && (
            <AButton
              type="dashed"
              onClick={() => setModalSign(true)}
              icon={<SignatureOutlined />}
              style={{ backgroundColor: '#33cf5c', color: '#fff' }}
            >
              Assinatura do profissional
            </AButton>
          )}
        </RowPrintDocument>
      </HeaderContainer>
      {validationCode && (
        <p style={{ marginTop: 8, fontWeight: 600 }}>
          Código de validação: {validationCode}
        </p>
      )}
      {signatureMode && (
        <p style={{ marginTop: 4, fontWeight: 600 }}>
          Modo da assinatura:{' '}
          {signatureMode === 'A1' ? 'Assinado com A1' : 'Assinatura interna'}
        </p>
      )}
      <DocumentReport
        report={report}
        signature={signature}
        validationCode={validationCode || report?.signature_validation_code}
      />
      <ToSignature
        open={modalSign && canUseDigitalSignature}
        close={() => setModalSign(false)}
        setSignature={setSignature}
        professional_id={report?.professional_id}
        onAuthenticated={async (authData, password) => {
          const response = await api.post('/signature-validation/sign', {
            document_type: isPrescription ? 'PRESCRIPTION' : 'DOCUMENT',
            record_id: id,
            professional_id: report?.professional_id,
            password,
          });

          setSignature(
            authData.signature || response.data.professional_signature || null,
          );
          setValidationCode(response.data.validation_code || null);
          setSignatureMode(response.data.signature_mode || null);
          addToast({
            type: 'success',
            title: isPrescription
              ? 'Receituário assinado'
              : 'Documento assinado',
            description: `${
              isPrescription ? 'Receituário' : 'Documento'
            } assinado digitalmente com sucesso.`,
          });
        }}
      />
    </>
  );
};

export default DocumentPrint;
